import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Icon } from '../Icon';
import { Loader } from '../Loader';
import { SubscriptionPlanSelectorV4 } from '../SubscriptionPlanSelectorV4';
import { SubscriptionPlanSelectorV9 } from '../SubscriptionPlanSelectorV9';
import { PlanSummaryCardV2 } from '../PlanSummaryCardV2';
import { Checkout } from '../Checkout';
import { FaqV2 } from '../FaqV2';
import { useSubscriptionPlans } from '../../shared/use-subscription-plans';
import { useIntersectionTracking } from '../../shared/use-intersection-tracking';
import { scrollToPlans } from '../../shared/scroll-helpers';
import { createSubscriptionForm } from '../../../lib/use-cases/create-subscription';
import { handleCreateSubscriptionError } from '../../shared/error-helpers';
import { resolveFirstStepUrl } from '../../../lib/shared/upsell-flow';
import { getCurrentPrelander } from '../../../lib/shared/prelander-helpers';
import { getMetadata } from '../../../lib/shared/ad-helpers';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { FP_PROMO_MAIN_TYPE } from '../../../lib/shared/promotion-type';
import { getChapterLabel, getChapterCategory } from '../../shared/chapter-answer-mapper';
import { getConcernLabel } from '../../shared/plan-summary-mapper';
import { getPlanDiscountPercent } from '../../shared/plan-helpers';
import { US_REGION } from '../../app-legal-regions';
import {
    MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN,
    MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN,
    MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN,
    MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN,
    MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN
} from '../../../lib/shared/subscription-plans';
import printedBookImageSrc from '../../assets/images/sale/sale-printed-book.png';
import digitalBookImageSrc from '../../assets/images/sale/sale-digital-book.png';
import ebookKeepsakeImageSrc from '../../assets/images/sale/sale-ebook-keepsake.png';
import ebookKeepsakeImageAgeBranchSrc from '../../assets/images/sale/sale-ebook-keepsake-2.png';
import styles from './SaleV31.css';

const plansWithoutPromo = [MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN, MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN, MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN];
const plansWithMainPromo = [MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN, MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN, MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN];
const popularPlans = [MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN, MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN];
const lettersBonusPlans = [
    MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN,
    MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN,
    MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN,
    MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN
];

const CHAPTER_LABEL_MAP = {
    1: 'Growing Up',
    2: 'First Job',
    3: 'A Hard Year',
    4: 'Who Raised You'
};

const cx = classnames.bind(styles);

const COMPARISON_COLUMNS = [
    {
        title: 'Printed book',
        imageSrc: printedBookImageSrc,
        icon: 'circleX',
        tone: 'printed',
        points: ['Printed once, no edits', 'Wait for print and delivery', 'Pay again for every copy', 'Lost or damaged, it’s gone', 'Deadlines, pressure, stress']
    },
    {
        title: 'Digital book',
        imageSrc: digitalBookImageSrc,
        icon: 'checkTick',
        tone: 'digital',
        points: ['Add memories any time', 'Start today, right now', 'Share with anyone, free', 'Safe in your account', 'No deadlines, no pressure']
    }
];

const PLAN_HIGHLIGHTS = [
    {
        title: 'No more “someday”',
        description: 'One question at a time turns “I should write this down” into something you’ve actually done.'
    },
    {
        title: 'The relief of starting',
        description: 'No blank page. We ask, you talk, and the memories come back on their own.'
    },
    {
        title: 'Details that stop slipping away',
        description: 'Names, places and dates get written down while you still remember them.'
    },
    {
        title: 'Confidence, not frustration',
        description: 'Talk or type — no writing skills or tech know-how needed.'
    },
    {
        title: 'Knowing they’ll know you',
        description: 'More than photos: your reasons, your voice, your life in your words. Private until you choose to share.'
    }
];

const CHAPTER_CATEGORY_MAP = {
    1: 'earlyChildhoodAndRoots',
    2: 'earlyAdulthood',
    3: 'maturityAndCareer',
    4: 'earlyChildhoodAndRoots'
};

function SaleV31({
    data,
    initialPrelander,
    expiryTimestamp,
    onExpirePromo = () => {},
    isExpired = true,
    selectSubscription,
    selectedSubscriptionData,
    merchantData,
    setMerchantData,
    legalLinks,
    legalRegion,
    isAgeBranch = false
}) {
    const isUsRegion = legalRegion === US_REGION;
    const { plans, isLoading } = useSubscriptionPlans([], isExpired);
    const keepsakeSrc = isAgeBranch ? ebookKeepsakeImageAgeBranchSrc : ebookKeepsakeImageSrc;
    const [isPending, setIsPending] = useState(false);
    const [activePlanData, setActivePlanData] = useState({});
    const [isLettersBonusOn, setIsLettersBonusOn] = useState(false);
    const firstUpsellUrl = resolveFirstStepUrl(getCurrentPrelander(), merchantData.subscriptionId, {
        hasLettersBonus: selectedSubscriptionData.hasLettersBonus
    });
    const chapterLabel = getChapterLabel(data.userResponses, CHAPTER_LABEL_MAP);
    const concernLabel = getConcernLabel(data.userResponses);

    useIntersectionTracking('.fp-block');

    const handleClose = () => {
        selectSubscription({});
        setMerchantData({});
        scrollToPlans();
    };

    useEffect(() => {
        const handleNavigation = e => {
            if (e.navigationType === 'traverse' && window.history.state?.checkoutVisible) {
                window.history.replaceState({ checkoutVisible: false }, '');
                handleClose();
            }
        };

        logEvent(EVENT_TYPES.SALE_PAGE_VISITED, { chapter: chapterLabel });

        window.navigation.addEventListener('navigate', handleNavigation);
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        return () => {
            window.navigation.removeEventListener('navigate', handleNavigation);
        };
    }, []);

    const getPlans = () => {
        const planOrder = !isExpired && data.promoType === FP_PROMO_MAIN_TYPE ? plansWithMainPromo : plansWithoutPromo;

        return planOrder
            .map(plan => plans.find(item => item.plan === plan))
            .filter(Boolean)
            .map(plan => ({
                ...plan,
                isPopular: popularPlans.includes(plan.plan),
                hasLettersBonus: lettersBonusPlans.includes(plan.plan)
            }));
    };

    const handleSelectPlan = plan => {
        setActivePlanData(plan);

        if (isLettersBonusOn && !plan.hasLettersBonus) {
            setIsLettersBonusOn(false);
            logEvent(EVENT_TYPES.LETTERS_BONUS_AUTO_DISABLED, { plan: plan.plan });
        }
    };

    const handleLettersBonusChange = isOn => {
        const bonusPlan = isOn && !activePlanData.hasLettersBonus ? getPlans().find(plan => plan.hasLettersBonus) : null;

        setIsLettersBonusOn(isOn);
        logEvent(EVENT_TYPES.LETTERS_BONUS_TOGGLED, { isOn, plan: (bonusPlan ?? activePlanData).plan });

        if (bonusPlan) {
            setActivePlanData(bonusPlan);
            logEvent(EVENT_TYPES.LETTERS_BONUS_PLAN_AUTO_SWITCHED, { fromPlan: activePlanData.plan, toPlan: bonusPlan.plan });
        }
    };

    useEffect(() => {
        const newPlans = getPlans();
        const defaultPlan = newPlans.find(plan => plan.isPopular) ?? newPlans[0];

        if (defaultPlan) {
            setActivePlanData(defaultPlan);
        }
    }, [plans, isExpired]);

    const submitSubscription = async subscriptionData => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                metadata: {
                    ...getMetadata(window.location.search, initialPrelander, data.userResponses),
                    memoryInitialChapter: getChapterCategory(data.userResponses, CHAPTER_CATEGORY_MAP),
                    ...(isLettersBonusOn && { features: ['lettersToMyself'] })
                }
            });

            window.history.pushState({ checkoutVisible: true }, '');
            setMerchantData(newMerchantData);

            if (newMerchantData?.fbEventId) {
                logEvent(EVENT_TYPES.SALE_CHECKOUT_INITIATE, { eventId: newMerchantData.fbEventId });
            }
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const tryAgain = async (subscriptionData, subscriptionId) => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                id: subscriptionId,
                metadata: {
                    ...getMetadata(window.location.search, initialPrelander, data.userResponses),
                    memoryInitialChapter: getChapterCategory(data.userResponses, CHAPTER_CATEGORY_MAP),
                    ...(isLettersBonusOn && { features: ['lettersToMyself'] })
                }
            });

            setMerchantData(newMerchantData);
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const handleSubscribe = subscriptionData => {
        logEvent(EVENT_TYPES.PLAN_SELECTED, { plan: subscriptionData.plan, letters: isLettersBonusOn });
        setMerchantData({});
        selectSubscription(subscriptionData);
        submitSubscription(subscriptionData);
    };

    if (merchantData.merchant && selectedSubscriptionData.plan) {
        return (
            <div className={styles.fixedModal}>
                <Checkout
                    merchantData={merchantData}
                    subscriptionData={selectedSubscriptionData}
                    promoType={isExpired ? '' : data.promoType}
                    onClose={handleClose}
                    tryAgain={tryAgain}
                    isPending={isPending}
                    successUrl={firstUpsellUrl}
                    termsOfUseLink={legalLinks.termsOfUse}
                    defaultDiscountValue={getPlanDiscountPercent(selectedSubscriptionData)}
                    submitLabel={isUsRegion ? 'Subscribe' : 'Pay and Get my plan'}
                    isWalletEnabled
                    isLettersBonusOn={isLettersBonusOn}
                />
            </div>
        );
    }

    const renderPlans = () => {
        const newPlans = getPlans();
        const PlanSelector = isUsRegion ? SubscriptionPlanSelectorV4 : SubscriptionPlanSelectorV9;

        return (
            <div className={styles.plansBlock}>
                {isLoading
                    ? <Loader className={styles.loader} variant='secondary' />
                    : (
                        <PlanSelector
                            onSubmit={handleSubscribe}
                            selectedSubscriptionData={activePlanData}
                            selectSubscription={handleSelectPlan}
                            isPending={isPending}
                            plans={newPlans}
                            expiryTimestamp={expiryTimestamp}
                            onExpirePromo={onExpirePromo}
                            isExpired={isExpired}
                            discountTitle='Your welcome price is applied!'
                            hasDiscountTimer={false}
                            termsOfUseLink={legalLinks.termsOfUse}
                            isLettersBonusOn={isLettersBonusOn}
                            onLettersBonusChange={handleLettersBonusChange}
                        />
                    )
                }
            </div>
        );
    };

    return (
        <div className={styles.root}>
            <section className={styles.comparison}>
                <h2 className={styles.sectionTitle}>Printed books get lost. Your stories won’t.</h2>
                <div className={styles.comparisonColumns}>
                    {COMPARISON_COLUMNS.map(column => (
                        <article className={cx('comparisonColumn', column.tone)} key={column.title}>
                            <img src={column.imageSrc} alt={column.title} className={styles.comparisonImage} />
                            <ul className={styles.comparisonPoints}>
                                {column.points.map(point => (
                                    <li className={styles.comparisonPoint} key={point}>
                                        <Icon name={column.icon} className={styles.comparisonIcon} />
                                        {point}
                                    </li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>
            </section>
            <div id='subscription-start-block' className={styles.subscriptionStartBlock} />
            <h2 className={styles.title}>
                {'Your '}<span className={styles.titleAccent}>Life Story Plan</span>{' is ready!'}
            </h2>
            <PlanSummaryCardV2 concernLabel={concernLabel} chapterLabel={chapterLabel} />
            <div id={EVENT_TYPES.FIRST_PLAN_VIEWED} className='fp-block' />
            {renderPlans()}
            <section className={styles.highlights}>
                <h2 className={styles.sectionTitle}>What changes for you</h2>
                <ul className={styles.highlightsList}>
                    {PLAN_HIGHLIGHTS.map(highlight => (
                        <li className={styles.highlight} key={highlight.title}>
                            <Icon name='checkItemFilled' className={styles.highlightIcon} />
                            <div className={styles.highlightCopy}>
                                <p className={styles.highlightTitle}>{highlight.title}</p>
                                <p className={styles.highlightDescription}>{highlight.description}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>
            <section className={styles.keepsake}>
                <div className={styles.keepsakePlate}>
                    <img src={keepsakeSrc} alt='' className={styles.keepsakeImage} />
                    <h2 className={styles.keepsakeTitle}>Something your family will keep</h2>
                    <p className={styles.keepsakeDescription}>
                        {'Chapter by chapter, '}<strong>your stories become an e-book</strong>{' you can download, print at home, or send to whoever you choose.'}
                    </p>
                </div>
            </section>
            <FaqV2 />
            <h2 className={styles.sectionTitle}>
                {'Someday is where stories disappear. Today, it’s '}<span className={styles.titleAccent}>one question.</span>
            </h2>
            {renderPlans()}
        </div>
    );
}

SaleV31.propTypes = {
    data: PropTypes.object.isRequired,
    expiryTimestamp: PropTypes.any,
    onExpirePromo: PropTypes.func,
    isExpired: PropTypes.bool,
    initialPrelander: PropTypes.string.isRequired,
    legalLinks: PropTypes.object.isRequired,
    legalRegion: PropTypes.string,
    isAgeBranch: PropTypes.bool
};

export {
    SaleV31
};
